import React, { useEffect, useRef } from 'react';
import FlvJs from 'flv.js';
import Hls from 'hls.js';
import PropTypes from 'prop-types';

export const HLS_EXTENSIONS = /\.(m3u8)($|\?)/i;
export const FLV_EXTENSIONS = /\.(flv)($|\?)/i;

const NETWORK_RECOVERY_DELAYS_MS = [0, 1000, 2000, 4000, 8000, 16000];
const MAX_MEDIA_RECOVERY_ATTEMPTS = 2;

/**
 * Enhanced Video tag to load live streamed videos.
 * Currently supports are the native video extensions plus HLS (.m3u8).
 *
 * @params every param is the same as the video tag aside from
 *
 * @param {string} playerId - the id for the video player
 * @param {string} format - explicitly state if an format to avoid inferring from url
 * @param {object} [playerRef] - the ref for the player
 */
const VideoPlayer = ({
  playerId,
  src,
  autoPlay = false,
  muted,
  controls,
  loop,
  preload,
  height = '100%',
  width = '100%',
  playerRef,
  onPlay,
  onPause,
  onVolumeChange,
  onKeyUp,
  authToken,
  format = '',
  onFatalError,
  controlsList = '',
  disablePictureInPicture = false,
}) => {
  const hls = useRef(null);
  const flv = useRef(null);
  const networkRecoveryAttempts = useRef(0);
  const mediaRecoveryAttempts = useRef(0);
  const recoveryTimer = useRef(null);
  const fatalErrorReported = useRef(false);
  const onFatalErrorRef = useRef(onFatalError);

  onFatalErrorRef.current = onFatalError;

  const reportFatalError = data => {
    if (!fatalErrorReported.current && onFatalErrorRef.current) {
      fatalErrorReported.current = true;
      onFatalErrorRef.current(data);
    }
  };

  const removeVideo = () => {
    if (recoveryTimer.current !== null) {
      clearTimeout(recoveryTimer.current);
      recoveryTimer.current = null;
    }
    if (hls.current) {
      hls.current.destroy();
      hls.current = null;
    }
    if (flv.current) {
      flv.current.destroy();
      flv.current = null;
    }
  };

  const setupVideo = () => {
    const video = document.getElementById(playerId);
    removeVideo();

    // The autoPlay attribute alone does not start an HLS.js stream: the
    // element has no source until a manifest has been parsed and a buffer
    // attached. Playback can still be refused (an unmuted stream with no
    // prior user interaction), and the controls remain available for that.
    const playIfRequested = () => {
      if (!autoPlay || !video) {
        return;
      }
      const started = video.play();
      if (started && typeof started.catch === 'function') {
        started.catch(() => {});
      }
    };

    networkRecoveryAttempts.current = 0;
    mediaRecoveryAttempts.current = 0;
    fatalErrorReported.current = false;

    if (format === 'hls' || (!format && HLS_EXTENSIONS.test(src))) {
      // API Docs: https://github.com/video-dev/hls.js/blob/master/docs/API.md
      if (Hls.isSupported()) {
        const hlsInstance = new Hls({
          ...(authToken && {
            xhrSetup: xhr => {
              xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
            },
          }),
        });
        hls.current = hlsInstance;

        hlsInstance.loadSource(src);
        hlsInstance.attachMedia(video);

        hlsInstance.on(Hls.Events.MANIFEST_PARSED, playIfRequested);

        // For this event, data will return an object with the following important keys
        // fatal (did it crash), details (specific error), and type (error category)
        hlsInstance.on(Hls.Events.ERROR, (error, data) => {
          if (!data.fatal || hls.current !== hlsInstance) {
            return;
          }

          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            if (recoveryTimer.current !== null) {
              return;
            }
            const attempt = networkRecoveryAttempts.current;
            if (attempt < NETWORK_RECOVERY_DELAYS_MS.length) {
              networkRecoveryAttempts.current += 1;
              // AIDEV-NOTE: Keep retries bounded. A cellular outage can make
              // MediaMTX return HLS 404s indefinitely.
              recoveryTimer.current = setTimeout(() => {
                recoveryTimer.current = null;
                if (hls.current === hlsInstance) {
                  hlsInstance.startLoad();
                }
              }, NETWORK_RECOVERY_DELAYS_MS[attempt]);
              return;
            }
          } else if (
            data.type === Hls.ErrorTypes.MEDIA_ERROR &&
            mediaRecoveryAttempts.current < MAX_MEDIA_RECOVERY_ATTEMPTS
          ) {
            mediaRecoveryAttempts.current += 1;
            hlsInstance.recoverMediaError();
            return;
          }

          reportFatalError(data);
          removeVideo();
        });

        hlsInstance.on(Hls.Events.FRAG_BUFFERED, () => {
          networkRecoveryAttempts.current = 0;
          mediaRecoveryAttempts.current = 0;
        });
      }
      // HLS.js is not supported on platforms that do not have Media Source
      // Extensions (MSE) enabled.
      //
      // When the browser has built-in HLS support (check using `canPlayType`),
      // we can provide an HLS manifest (i.e. .m3u8 URL) directly to the video
      // element through the `src` property. This is using the built-in support
      // of the plain video element, without using HLS.js.
      //
      // Note: it would be more normal to wait on the 'canplay' event below however
      // on Safari (where you are most likely to find built-in HLS support) the
      // video.src URL must be on the user-driven white-list before a 'canplay'
      // event will be emitted; the last video event that can be reliably
      // listened-for when the URL is not on the white-list is 'loadedmetadata'.
      else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = src;
      }
    } else if (format === 'flv' || (!format && FLV_EXTENSIONS.test(src))) {
      // API Docs: https://github.com/Bilibili/flv.js/blob/master/docs/api.md
      FlvJs.LoggingControl.enableVerbose = false;
      flv.current = FlvJs.createPlayer(
        {
          type: 'flv',
          url: src,
          isLive: true,
          duration: 5000,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      flv.current.on(FlvJs.Events.ERROR, event => {
        onFatalError(event);
      });

      flv.current.attachMediaElement(document.getElementById(playerId));
      flv.current.load();
    } else {
      video.src = src;
    }
  };

  useEffect(() => {
    setupVideo();

    return () => {
      removeVideo();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  return (
    // A source change gets a brand new element rather than a reused one.
    // Swapping streams in place leaves the element holding the previous
    // MediaSource — whose object URL the outgoing Hls instance has already
    // revoked — and the picture can stay frozen on the old stream.
    <video
      key={src}
      id={playerId}
      ref={playerRef}
      muted={muted}
      autoPlay={autoPlay}
      controls={controls}
      loop={loop}
      preload={preload}
      height={height}
      width={width}
      onPlay={onPlay}
      onPause={onPause}
      onVolumeChange={onVolumeChange}
      onKeyUp={onKeyUp}
      controlsList={controlsList}
      disablePictureInPicture={disablePictureInPicture}
    ></video>
  );
};

VideoPlayer.propTypes = {
  playerId: PropTypes.string.isRequired,
  src: PropTypes.string,
  muted: PropTypes.bool,
  autoPlay: PropTypes.bool,
  controls: PropTypes.bool,
  loop: PropTypes.bool,
  preload: PropTypes.oneOf(['none', 'metadata', 'auto', '']),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  playerRef: PropTypes.object,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onVolumeChange: PropTypes.func,
  onKeyUp: PropTypes.func,
  authToken: PropTypes.string,
  format: PropTypes.oneOf(['hls', 'flv', '']),
  onFatalError: PropTypes.func,
  controlsList: PropTypes.string,
  disablePictureInPicture: PropTypes.bool,
};

export default VideoPlayer;
